import React, { useState, useRef, useEffect, useCallback } from 'react';
import T from 'prop-types';
// utils
import { isEqual } from 'lodash';
// components
import Typography from 'components/Typography';
// icons
import { ReactComponent as SortIcon } from 'icons/sort-indicator.svg';
// styles
import classnames from 'classnames/bind';
import styles from './Table.module.scss';

const cx = classnames.bind(styles);

const Table = ({ columns = [], data = [], assessmentRowRenderer = null, className }) => {
  const [tableData, setTableData] = useState([]);
  const sortStateRefs = useRef({});

  useEffect(() => {
    columns.forEach((_, idx) => {
      sortStateRefs.current = { ...sortStateRefs.current, [idx]: false };
    });
  }, []);

  useEffect(() => {
    if (!isEqual(data, tableData)) {
      setTableData(data);
    }
  }, [data]);

  const handleSortChange = useCallback((sortFunc, idx) => {
    sortStateRefs.current = { ...sortStateRefs.current, [idx]: !sortStateRefs.current[idx] };
    setTableData((prev) => prev.slice().sort(sortFunc));
  }, []);

  return (
    <div className={cx('table-wrapper')}>
      <table className={cx('table', 'mgb-20', className)}>
        <thead>
          <tr className={cx('table-head')}>
            {columns.map(({ name, onClick = null, sortFunction = null, width = 100, centered }, idx) => (
              <td key={`name-${idx}`} style={{ width, maxWidth: width }} className="pdb-5">
                <div
                  className={cx('table-head--cell', {
                    sortable: !!sortFunction,
                    'clickable-text': !!sortFunction,
                    centered,
                  })}
                  onClick={() => !!sortFunction && handleSortChange(sortFunction(sortStateRefs.current[idx]), idx)}
                >
                  {
                    <Typography type="body-xs" onClick={onClick}>
                      {name}
                    </Typography>
                  }
                  {!!sortFunction && (
                    <SortIcon className={cx('sort-icon', 'mgl-5', { sorted: sortStateRefs.current[idx] })} />
                  )}
                </div>
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {assessmentRowRenderer?.()}
          {Array(tableData.length)
            .fill(null)
            .map((_, idx) => (
              <tr key={`row-${idx}`} className={cx('table-row')}>
                {Array(columns.length)
                  .fill(null)
                  .map((_, _idx) => (
                    <td
                      key={`key-${_idx}`}
                      style={{
                        boxShadow: columns[_idx]?.centered && '-1px 0px 0px #d0d1d7',
                        maxWidth: columns[_idx].width,
                      }}
                    >
                      {columns[_idx]?.renderer(tableData[idx])}
                    </td>
                  ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  columns: T.arrayOf(T.object),
  data: T.arrayOf(T.object),
  assessmentRowRenderer: T.func,
  className: T.oneOfType([T.func, T.string]),
};

export default Table;
